import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ClipboardList, Loader2, Eye, FileEdit, ShieldCheck, ShieldAlert, ShieldOff, Shield, AlertTriangle, Trash2, Calendar, Archive, ArchiveRestore, Filter, Pencil } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

import { ChecklistDetalleDialog } from './ChecklistDetalleDialog';
import { useNavigate } from 'react-router-dom';
import { EditarMetadataChecklistDialog } from './EditarMetadataChecklistDialog';
import { getSystemTypeLabel } from '@/lib/systemTypeConstants';
import { hasChecklistTemplate } from '@/lib/checklists/registry';
import { calcularCobertura, tipoProximaLabel, type CoberturaResult } from '@/lib/coberturaRevisiones';
import { toast } from 'sonner';

interface ChecklistsClienteTabProps {
  clienteId: string;
  clienteNombre: string;
}

interface ChecklistSeguridad {
  id: string;
  fecha: string;
  estado: string;
  tipo_revision: string;
  sistema_id: string | null;
  observaciones_generales: string | null;
  firma_tecnico: string | null;
  created_at: string;
  archivado: boolean;
  periodo: string | null;
  numero_trimestral: number | null;
  perfiles: { nombre: string } | null;
}

interface SistemaMin {
  id: string;
  tipo: string;
  ubicacion: string | null;
  cobertura_omitida: boolean;
  revision_ancla_periodo?: string | null;
  revision_ancla_tipo?: string | null;
}

interface CoberturaSistema {
  sistema: SistemaMin;
  result: CoberturaResult;
}

const estadoBadge: Record<string, { label: string; className: string }> = {
  en_progreso: { label: 'Borrador', className: 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-400 border-yellow-500/30' },
  completado: { label: 'Completado', className: 'bg-green-500/20 text-green-700 dark:text-green-400 border-green-500/30' },
};

const tipoRevisionLabel: Record<string, string> = {
  trimestral: 'Trimestral',
  semestral: 'Semestral',
  anual: 'Anual',
};

export const ChecklistsClienteTab: React.FC<ChecklistsClienteTabProps> = ({ clienteId, clienteNombre }) => {
  const [checklists, setChecklists] = useState<ChecklistSeguridad[]>([]);
  const [sistemas, setSistemas] = useState<SistemaMin[]>([]);
  const [coberturas, setCoberturas] = useState<CoberturaSistema[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedChecklist, setSelectedChecklist] = useState<ChecklistSeguridad | null>(null);
  const navigate = useNavigate();
  const irARevision = (opts: { sistemaId: string; sistemaTipo: string; checklistId?: string }) => {
    const params = new URLSearchParams({
      sistemaId: opts.sistemaId,
      sistemaTipo: opts.sistemaTipo,
      clienteId,
      clienteNombre: clienteNombre || '',
      returnTo: window.location.pathname + window.location.search,
    });
    if (opts.checklistId) params.set('checklistId', opts.checklistId);
    navigate(`/checklist-revision?${params.toString()}`);
  };
  const [borradorAEliminar, setBorradorAEliminar] = useState<string | null>(null);
  const [eliminando, setEliminando] = useState(false);
  const [mostrarArchivados, setMostrarArchivados] = useState(false);
  const [filtroTipo, setFiltroTipo] = useState<string>('');
  const [filtroSistema, setFiltroSistema] = useState<string>('');
  const [editandoMetadata, setEditandoMetadata] = useState<ChecklistSeguridad | null>(null);
  const [fixingTrimestral, setFixingTrimestral] = useState<string | null>(null);

  const cargar = async () => {
    setLoading(true);
    // Checklists del cliente (excluimos modo='elementos' = revisión rápida)
    const { data: checklistsData } = await (supabase as any)
      .from('checklists_seguridad')
      .select('id, fecha, estado, sistema_id, observaciones_generales, firma_tecnico, created_at, tipo_revision, archivado, periodo, modo, numero_trimestral, perfiles!checklists_seguridad_tecnico_id_fkey(nombre)')
      .eq('cliente_id', clienteId)
      .neq('tipo_revision', 'rapida')
      .neq('modo', 'elementos')
      .order('created_at', { ascending: false });

    // Sistemas del cliente (para cobertura)
    const { data: sistemasData } = await supabase
      .from('sistemas')
      .select('id, tipo, ubicacion, cliente_id, cobertura_omitida, revision_ancla_periodo, revision_ancla_tipo' as any)
      .eq('cliente_id', clienteId)
      .eq('activo', true);

    const sistemasList: SistemaMin[] = (sistemasData || []).map((s: any) => ({
      id: s.id,
      tipo: s.tipo,
      ubicacion: s.ubicacion,
      cobertura_omitida: !!s.cobertura_omitida,
      revision_ancla_periodo: s.revision_ancla_periodo ?? null,
      revision_ancla_tipo: s.revision_ancla_tipo ?? null,
    }));

    // Listado: mostramos TODOS los checklists del cliente (sin dedupe).
    // El dedupe por (sistema, tipo, periodo) solo se aplica al cálculo de cobertura,
    // para evitar contar dos veces la misma revisión normativa.
    const checklistsRaw = (checklistsData || []) as any[];
    setChecklists(checklistsRaw as unknown as ChecklistSeguridad[]);
    setSistemas(sistemasList);

    // Cobertura por sistema según calendario normativo (con dedupe interno)
    const sistemasConChecklist = sistemasList.filter(s => hasChecklistTemplate(s.tipo));
    const seenCob = new Set<string>();
    const porSistema: Record<string, any[]> = {};
    checklistsRaw.forEach((c: any) => {
      if (!c.sistema_id) return;
      if (c.archivado) return; // los archivados no cuentan
      const fechaObj = c.fecha ? new Date(c.fecha) : new Date(c.created_at);
      const periodoFb = `${fechaObj.getFullYear()}-${String(fechaObj.getMonth() + 1).padStart(2, '0')}`;
      const key = `${c.sistema_id}|${c.tipo_revision || 'sin'}|${c.periodo || periodoFb}`;
      if (seenCob.has(key)) return;
      seenCob.add(key);
      (porSistema[c.sistema_id] ||= []).push(c);
    });
    const cob: CoberturaSistema[] = sistemasConChecklist.map(s => ({
      sistema: s,
      result: calcularCobertura(
        s.tipo,
        (porSistema[s.id] || []).map((c: any) => ({
          fecha: c.fecha || c.created_at,
          periodo: c.periodo,
          tipo_revision: c.tipo_revision,
          estado: c.estado,
          numero_trimestral: c.numero_trimestral,
        })),
        s.cobertura_omitida,
        new Date(),
        s.revision_ancla_periodo
          ? { periodo: s.revision_ancla_periodo, tipo: (s.revision_ancla_tipo ?? 'anual') as any }
          : null,
      ),
    }));
    setCoberturas(cob);
    setLoading(false);
  };

  const toggleOmitirCobertura = async (sistemaId: string, valor: boolean) => {
    const { error } = await (supabase as any)
      .from('sistemas')
      .update({ cobertura_omitida: valor })
      .eq('id', sistemaId);
    if (error) {
      toast.error('No se pudo actualizar la cobertura');
      return;
    }
    toast.success(valor ? 'Sistema marcado sin mantenimiento' : 'Cobertura reactivada');
    cargar();
  };

  const handleEliminarBorrador = async (checklistId: string) => {
    setEliminando(true);
    try {
      await supabase.from('checklist_respuestas').delete().eq('checklist_id', checklistId);
      await supabase.from('checklist_elementos_probados').delete().eq('checklist_id', checklistId);
      await supabase.from('checklist_cabeceras').delete().eq('checklist_id', checklistId);
      const { error } = await supabase.from('checklists_seguridad').delete().eq('id', checklistId);
      if (error) throw error;
      await cargar();
    } catch (e) {
      console.error('Error eliminando borrador', e);
    } finally {
      setEliminando(false);
      setBorradorAEliminar(null);
    }
  };

  const handleToggleArchivar = async (checklistId: string, archivar: boolean) => {
    const { error } = await supabase
      .from('checklists_seguridad')
      .update({ archivado: archivar } as any)
      .eq('id', checklistId);
    if (error) {
      console.error('Error archivando checklist', error);
      return;
    }
    await cargar();
  };

  useEffect(() => { cargar(); }, [clienteId]);

  const sistemaMap = useMemo(() => {
    const m: Record<string, SistemaMin> = {};
    sistemas.forEach(s => { m[s.id] = s; });
    return m;
  }, [sistemas]);

  // Última revisión trimestral no archivada por sistema (para fix rápido de nº trimestral)
  const ultimaTrimestralPorSistema = useMemo(() => {
    const m: Record<string, ChecklistSeguridad> = {};
    const ordenados = [...checklists]
      .filter(c => !c.archivado && c.tipo_revision === 'trimestral' && (c.estado === 'completado' || c.estado === 'finalizada'))
      .sort((a, b) => {
        const fa = a.periodo || a.fecha;
        const fb = b.periodo || b.fecha;
        return fb.localeCompare(fa);
      });
    for (const c of ordenados) {
      if (!c.sistema_id) continue;
      if (!m[c.sistema_id]) m[c.sistema_id] = c;
    }
    return m;
  }, [checklists]);

  const fijarNumeroTrimestral = async (checklistId: string, numero: number) => {
    setFixingTrimestral(checklistId);
    const { error } = await (supabase as any)
      .from('checklists_seguridad')
      .update({ numero_trimestral: numero })
      .eq('id', checklistId);
    setFixingTrimestral(null);
    if (error) {
      toast.error('No se pudo actualizar: ' + error.message);
      return;
    }
    toast.success(`Marcada como ${numero}ª trimestral`);
    cargar();
  };


  const checklistsFiltrados = useMemo(() => {
    return checklists.filter(c => {
      const coincideArchivado = mostrarArchivados ? c.archivado : !c.archivado;
      const coincideTipo = filtroTipo ? c.tipo_revision === filtroTipo : true;
      const coincideSistema = filtroSistema ? c.sistema_id === filtroSistema : true;
      return coincideArchivado && coincideTipo && coincideSistema;
    });
  }, [checklists, mostrarArchivados, filtroTipo, filtroSistema]);

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Cobertura de revisiones */}
      {coberturas.length > 0 && (() => {
        const resumen = coberturas.reduce(
          (acc, c) => {
            acc[c.result.estado] = (acc[c.result.estado] || 0) + 1;
            return acc;
          },
          {} as Record<string, number>,
        );
        return (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <CardTitle className="flex items-center gap-2 text-base">
                  <ShieldCheck className="h-5 w-5" />
                  Cobertura de revisiones
                </CardTitle>
                <div className="flex items-center gap-2 text-xs">
                  {(resumen.al_dia || resumen.proximo) ? (
                    <Badge className="bg-green-500/20 text-green-700 dark:text-green-400 border-green-500/30" variant="outline">
                      {(resumen.al_dia || 0) + (resumen.proximo || 0)} al día
                    </Badge>
                  ) : null}
                  {resumen.vencido ? (
                    <Badge className="bg-red-500/20 text-red-700 dark:text-red-400 border-red-500/30" variant="outline">
                      {resumen.vencido} vencidas
                    </Badge>
                  ) : null}
                  {resumen.sin_iniciar ? (
                    <Badge variant="outline">{resumen.sin_iniciar} sin iniciar</Badge>
                  ) : null}
                  {resumen.omitido ? (
                    <Badge variant="outline" className="text-muted-foreground">
                      {resumen.omitido} omitidos
                    </Badge>
                  ) : null}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {coberturas.map(c => {
                const r = c.result;
                const Icono =
                  r.estado === 'al_dia' ? ShieldCheck
                  : r.estado === 'proximo' ? ShieldCheck
                  : r.estado === 'vencido' ? ShieldAlert
                  : r.estado === 'omitido' ? ShieldOff
                  : r.estado === 'requiere_aclaracion' ? AlertTriangle
                  : Shield;
                const colorIcono =
                  r.estado === 'al_dia' ? 'text-green-600'
                  : r.estado === 'proximo' ? 'text-amber-600'
                  : r.estado === 'vencido' ? 'text-red-600'
                  : r.estado === 'omitido' ? 'text-muted-foreground'
                  : r.estado === 'requiere_aclaracion' ? 'text-amber-600'
                  : 'text-muted-foreground';
                const textoEstado =
                  r.estado === 'al_dia' ? 'Al día'
                  : r.estado === 'proximo' ? `Próxima en ${Math.abs(r.diasDesfase)} días`
                  : r.estado === 'vencido' ? `Vencida hace ${r.diasDesfase} días`
                  : r.estado === 'omitido' ? 'Sin mantenimiento'
                  : r.estado === 'requiere_aclaracion' ? 'Indica nº de trimestral (1T/2T/3T)'
                  : 'Pendiente de primera revisión';
                return (
                  <div
                    key={c.sistema.id}
                    className="flex items-center justify-between gap-3 rounded-md border p-3"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <Icono className={`h-7 w-7 shrink-0 ${colorIcono}`} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium truncate">{getSystemTypeLabel(c.sistema.tipo)}</span>
                          {c.sistema.ubicacion && (
                            <span className="text-muted-foreground text-xs truncate">· {c.sistema.ubicacion}</span>
                          )}
                          <Badge
                            variant="outline"
                            className={
                              r.estado === 'al_dia' ? 'bg-green-500/20 text-green-700 dark:text-green-400 border-green-500/30'
                              : r.estado === 'proximo' ? 'bg-amber-500/20 text-amber-700 dark:text-amber-400 border-amber-500/30'
                              : r.estado === 'vencido' ? 'bg-red-500/20 text-red-700 dark:text-red-400 border-red-500/30'
                              : r.estado === 'requiere_aclaracion' ? 'bg-amber-500/20 text-amber-700 dark:text-amber-400 border-amber-500/30'
                              : ''
                            }
                          >
                            {textoEstado}
                          </Badge>
                        </div>
                        {r.estado !== 'omitido' && (
                          <div className="text-xs text-muted-foreground mt-0.5 space-y-0.5">
                            <div>
                              {r.ultima ? (
                                <>
                                  Última: {format(r.ultima.fecha, "MMM yyyy", { locale: es })} (
                                  {r.ultima.tipo === 'trimestral' && r.ultima.numeroTrimestral
                                    ? `${r.ultima.numeroTrimestral}T`
                                    : r.ultima.tipo === 'trimestral'
                                      ? 'trimestral (sin nº)'
                                      : 'anual'}
                                  )
                                </>
                              ) : (
                                <>Sin revisiones previas</>
                              )}
                              {r.proxima && (
                                <> · <strong className="text-foreground">Próxima: {tipoProximaLabel(r.proxima.tipo)}</strong> — {format(r.proxima.fecha, "MMM yyyy", { locale: es })}</>
                              )}
                            </div>
                            {r.proximaAnual && (!r.proxima || r.proxima.tipo !== 'anual') && (
                              <div>
                                Próxima Anual: {format(r.proximaAnual.fecha, "MMM yyyy", { locale: es })}
                              </div>
                            )}
                            {r.estado === 'requiere_aclaracion' && (() => {
                              const last = ultimaTrimestralPorSistema[c.sistema.id];
                              return (
                                <div className="space-y-1.5 mt-1">
                                  <div className="text-amber-700 dark:text-amber-400">
                                    La última trimestral no indica si era 1ª, 2ª o 3ª. Márcala aquí:
                                  </div>
                                  {last ? (
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                      {([1, 2, 3] as const).map((n) => (
                                        <Button
                                          key={n}
                                          size="sm"
                                          variant="outline"
                                          className="h-7 px-2.5 text-xs"
                                          disabled={fixingTrimestral === last.id}
                                          onClick={(e) => { e.stopPropagation(); fijarNumeroTrimestral(last.id, n); }}
                                        >
                                          {n}T
                                        </Button>
                                      ))}
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-7 px-2 text-xs gap-1"
                                        onClick={(e) => { e.stopPropagation(); setEditandoMetadata(last); }}
                                      >
                                        <Pencil className="h-3 w-3" /> Editar más
                                      </Button>
                                    </div>
                                  ) : (
                                    <div className="text-xs text-muted-foreground">
                                      No se encontró ninguna trimestral activa que editar.
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Label htmlFor={`omitir-${c.sistema.id}`} className="text-xs text-muted-foreground cursor-pointer">
                        Omitir
                      </Label>
                      <Switch
                        id={`omitir-${c.sistema.id}`}
                        checked={c.sistema.cobertura_omitida}
                        onCheckedChange={(v) => toggleOmitirCobertura(c.sistema.id, v)}
                      />
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        );
      })()}


      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5" />
              Checklists de Seguridad
              <Badge variant="secondary" className="ml-2">
                {checklistsFiltrados.length}
              </Badge>
            </CardTitle>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-1.5">
                <Filter className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs text-muted-foreground hidden sm:inline">Sistema:</span>
                <Select value={filtroSistema || '__all__'} onValueChange={(v) => setFiltroSistema(v === '__all__' ? '' : v)}>
                  <SelectTrigger className="h-7 text-xs w-[160px]">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todos</SelectItem>
                    {sistemas.map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        {getSystemTypeLabel(s.tipo)}
                        {s.ubicacion ? ` · ${s.ubicacion}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1.5">
                <Filter className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs text-muted-foreground hidden sm:inline">Tipo:</span>
                {(['', 'trimestral', 'semestral', 'anual'] as const).map((t) => (
                  <Button
                    key={t || 'all'}
                    variant={filtroTipo === t ? 'default' : 'outline'}
                    size="sm"
                    className="h-7 text-xs px-2.5"
                    onClick={() => setFiltroTipo(t)}
                  >
                    {t ? tipoRevisionLabel[t] || t : 'Todos'}
                  </Button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Switch id="ck-archivados" checked={mostrarArchivados} onCheckedChange={setMostrarArchivados} />
                <Label htmlFor="ck-archivados" className="text-sm cursor-pointer">
                  Ver archivados
                </Label>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {checklistsFiltrados.length === 0 ? (
            <div className="text-center py-8">
              <ClipboardList className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
              <p className="text-muted-foreground">
                {mostrarArchivados ? 'No hay checklists archivados' : 'No hay checklists de seguridad para este cliente'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[120px]">Periodo (MM-AAAA)</TableHead>
                    <TableHead>Tipo revisión</TableHead>
                    <TableHead>Sistema</TableHead>
                    <TableHead>Técnico</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {checklistsFiltrados.map((checklist) => {
                    const estado = estadoBadge[checklist.estado] || { label: checklist.estado, className: '' };
                    const sistema = checklist.sistema_id ? sistemaMap[checklist.sistema_id] : null;
                    const esBorrador = checklist.estado === 'en_progreso';
                    const fechaObj = new Date(checklist.fecha);
                    const periodoFallback = `${fechaObj.getFullYear()}-${String(fechaObj.getMonth() + 1).padStart(2, '0')}`;
                    const periodoRaw = checklist.periodo || periodoFallback;
                    const mmYyyy = /^(\d{4})-(\d{2})$/.test(periodoRaw)
                      ? `${periodoRaw.slice(5)}-${periodoRaw.slice(0,4)}`
                      : periodoRaw;
                    return (
                      <TableRow key={checklist.id}>
                        <TableCell>
                          <div className="flex items-center gap-2 font-mono font-semibold text-base">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            {mmYyyy}
                          </div>
                          <div className="text-[10px] text-muted-foreground mt-0.5">
                            Ejecutado: {format(fechaObj, 'dd/MM/yyyy', { locale: es })}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="default" className="text-xs capitalize">
                            {checklist.tipo_revision === 'trimestral' && checklist.numero_trimestral
                              ? `${checklist.numero_trimestral}T Trimestral`
                              : checklist.tipo_revision === 'trimestral'
                                ? 'Trimestral (sin nº)'
                                : tipoRevisionLabel[checklist.tipo_revision] || checklist.tipo_revision}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          {sistema ? getSystemTypeLabel(sistema.tipo) : '—'}
                        </TableCell>
                        <TableCell>
                          {checklist.perfiles?.nombre || '—'}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={estado.className}>
                            {estado.label}
                          </Badge>
                        </TableCell>

                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {esBorrador && sistema && (
                              <>
                                <Button
                                  variant="default"
                                  size="sm"
                                  className="gap-1"
                                  onClick={() => irARevision({
                                    sistemaId: sistema.id,
                                    sistemaTipo: sistema.tipo,
                                    checklistId: checklist.id,
                                  })}
                                >
                                  <FileEdit className="h-3.5 w-3.5" />
                                  Continuar
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                  onClick={() => setBorradorAEliminar(checklist.id)}
                                  title="Eliminar borrador"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setSelectedChecklist(checklist)}
                              title="Ver detalle"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setEditandoMetadata(checklist)}
                              title="Editar periodo, tipo o nº trimestral"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleToggleArchivar(checklist.id, !checklist.archivado)}
                              title={checklist.archivado ? 'Desarchivar' : 'Archivar'}
                            >
                              {checklist.archivado ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {selectedChecklist && (
        <ChecklistDetalleDialog
          open={!!selectedChecklist}
          onOpenChange={(open) => { if (!open) setSelectedChecklist(null); }}
          checklistId={selectedChecklist.id}
          fecha={selectedChecklist.fecha}
          tipoRevision={selectedChecklist.tipo_revision}
          tecnicoNombre={selectedChecklist.perfiles?.nombre || '—'}
          clienteNombre={clienteNombre}
          observacionesGenerales={selectedChecklist.observaciones_generales}
          firmaTecnico={selectedChecklist.firma_tecnico}
          sistemaTipo={selectedChecklist.sistema_id ? sistemaMap[selectedChecklist.sistema_id]?.tipo : undefined}
        />
      )}


      {editandoMetadata && (
        <EditarMetadataChecklistDialog
          open={!!editandoMetadata}
          onOpenChange={(o) => { if (!o) setEditandoMetadata(null); }}
          checklistId={editandoMetadata.id}
          initial={{
            periodo: editandoMetadata.periodo,
            tipo_revision: editandoMetadata.tipo_revision,
            numero_trimestral: editandoMetadata.numero_trimestral,
            fecha: editandoMetadata.fecha,
          }}
          onSaved={() => cargar()}
        />
      )}

      <AlertDialog open={!!borradorAEliminar} onOpenChange={(o) => { if (!o) setBorradorAEliminar(null); }}>

        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar borrador</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción eliminará permanentemente el borrador de checklist y todas sus respuestas. No se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setBorradorAEliminar(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => borradorAEliminar && handleEliminarBorrador(borradorAEliminar)}
              disabled={eliminando}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {eliminando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
